import React, { useState } from "react";
import type {
  TravelDetailsData,
  PlanSelection,
} from "../../types/customer-portal";
import { usePlanSelection } from "../../contexts/PlanSelectionContext";
import { useVerifyEmailAddress } from "../../hooks/useVerifyEmailAddress";
import { validateEmailLength, EMAIL_MAX_LENGTH } from "../../../../shared/utils/fieldValidation";
import "./EmailValidationSection.css";
import type {
  PrincipalBeneficiaryResponseType,
  PrincipalFamilyMemberResponseType,
  PrincipalInsuredAddressResponseType,
  PrincipalInsuredContactInfoResponseType,
  PrincipalInsuredResponseType,
} from "@/types/app";
import {
  transformPrincipalBeneficiariesFromResponse,
  transformPrincipalFamilyMemberFromResponse,
  transformPrincipalInsuredAddressFromResponse,
  transformPrincipalInsuredContactInfoFromResponse,
  transformPrincipalInsuredFromResponse,
} from "@/utils/parsers";
import { useProductPlanStore } from "@/store/useProductPlanStore";
import { useAppStore } from "@/store/useAppStore";

interface EmailValidationSectionProps {
  travelData?: TravelDetailsData;
  planSelection?: PlanSelection;
  onNext?: () => void;
  onEmailSent?: (email: string) => void;
}

const EmailValidationSection: React.FC<EmailValidationSectionProps> = ({
  travelData,
  planSelection,
  onNext,
  onEmailSent,
}) => {

  const { productPlanOption } = useProductPlanStore();
  const { setTempDataId } = useAppStore();

  console.log("EmailValidationSection planSelection:", planSelection);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  // Get selected plan, plan section, principal data methods, and tempData from context
  const { selectedPlan, planSection, setPrincipalData, setTempData, tempData } =
    usePlanSelection();

  // Use the email verification hook
  const {
    verifyEmail,
    loading: isValidating,
    error: apiError,
    clearState,
  } = useVerifyEmailAddress();

  // ENHANCED: Create effective planSelection with better fallback logic
  const effectivePlanSelection: PlanSelection | undefined =
    planSelection ||
    (selectedPlan
      ? {
          planType: selectedPlan.planType as PlanSelection["planType"],
          section: (planSection as PlanSelection["section"]) || "Domestic", // Fallback to 'Domestic' if planSection is null
        }
      : undefined);

  // Enhanced logging for debugging
  console.log("EmailValidationSection: Context state check:", {
    selectedPlan: !!selectedPlan,
    planSection,
    planSelectionProp: !!planSelection,
    effectivePlanSelection,
    selectedPlanType: selectedPlan?.planType,
    contextDebug: {
      hasSelectedPlan: !!selectedPlan,
      hasPlanSection: !!planSection,
      planSectionValue: planSection,
    },
  });

  // Email validation regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmailFormat = (email: string): boolean => {
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    // Clear errors when user starts typing
    if (emailError) {
      setEmailError("");
    }
    if (apiError) {
      clearState();
    }
  };

  const handleValidate = async () => {
    // Clear previous states
    setEmailError("");
    clearState();

    // SAFETY MEASURE: Clear any stale email verification data when starting new validation
    // This prevents contamination between different travel sessions
    localStorage.removeItem("emailVerificationComplete");
    localStorage.removeItem("emailVerificationTempData");
    localStorage.removeItem("emailVerificationPlanType");
    localStorage.removeItem("emailVerificationShouldSetupClient");
    localStorage.removeItem("emailVerificationTimestamp");
    localStorage.removeItem("currentVerificationId");
    sessionStorage.removeItem("emailVerificationComplete");
    sessionStorage.removeItem("emailVerificationTempData");
    sessionStorage.removeItem("emailVerificationPlanType");
    sessionStorage.removeItem("emailVerificationShouldSetupClient");
    localStorage.removeItem("emailVerificationTempDataBackup");

    console.log(
      "EmailValidationSection: Cleared stale email verification data before new validation"
    );

    // Validate email length first
    if (!validateEmailLength(email.trim())) {
      setEmailError(`E-mail cannot exceed ${EMAIL_MAX_LENGTH} characters.`);
      return;
    }

    // Validate email format
    if (!validateEmailFormat(email.trim())) {
      setEmailError("E-mail is invalid. Please enter a valid e-mail address.");
      return;
    }

    // Validate required data
    if (!travelData) {
      setEmailError(
        "Travel data is missing. Please go back and complete your travel information."
      );
      return;
    }

    // Enhanced logging for debugging
    console.log(
      "EmailValidationSection validation - planSelection prop:",
      planSelection
    );
    console.log(
      "EmailValidationSection validation - planSection context:",
      planSection
    );
    console.log(
      "EmailValidationSection validation - selectedPlan context:",
      selectedPlan
    );
    console.log(
      "EmailValidationSection validation - effectivePlanSelection:",
      productPlanOption
    );

    // ENHANCED: More robust validation with better error messages
    if (!productPlanOption) {
      console.error(
        "EmailValidationSection: No effective plan selection available",
        {
          planSelectionProp: planSelection,
          selectedPlan: !!selectedPlan,
          planSection,
          contextState: { selectedPlan: !!selectedPlan, planSection },
        }
      );
      setEmailError(
        "Plan selection is missing. Please go back and select a plan."
      );
      return;
    }

    if (!selectedPlan) {
      console.error("EmailValidationSection: No selected plan in context", {
        productPlanOption,
        planSection,
        contextState: { selectedPlan: !!selectedPlan, planSection },
      });
      setEmailError(
        "Selected plan information is missing. Please go back and select a plan."
      );
      return;
    }

    // Additional validation to ensure we have the minimum required data
    if (!productPlanOption.planType) {
      console.error(
        "EmailValidationSection: Missing planType in effective plan selection",
        productPlanOption
      );
      setEmailError(
        "Plan type information is missing. Please go back and select a plan."
      );
      return;
    }

    if (!productPlanOption.section) {
      console.error(
        "EmailValidationSection: Missing section in effective plan selection",
        productPlanOption
      );
      setEmailError(
        "Plan section information is missing. Please go back and select a plan."
      );
      return;
    }

    try {
      console.log("Sending email verification with data:", {
        email: email.trim(),
        travelData,
        planSelection: productPlanOption,
        selectedPlan,
      });

      // Call the email verification hook
      const result = await verifyEmail(
        email.trim(),
        travelData,
        productPlanOption,
        selectedPlan
      );

      if (result.success) {
        console.log("Email verification sent successfully");

        // CRITICAL FIX: Save email to session storage and tempData context for persistence
        const emailData = {
          email: email.trim(),
          isVerified: result.isVerified,
          timestamp: Date.now(),
        };

        // Save to session storage for immediate access
        sessionStorage.setItem(
          "emailVerificationTempData",
          JSON.stringify(emailData)
        );
        localStorage.setItem(
          "emailVerificationTempData",
          JSON.stringify(emailData)
        );

        // Save to tempData context for comprehensive access
        const currentTempData =
          tempData && typeof tempData === "object"
            ? (tempData as Record<string, unknown>)
            : {};
        setTempData({
          ...currentTempData,
          email: email.trim(),
          emailVerified: result.isVerified,
          emailTimestamp: Date.now(),
        });


        if(result.tempDataId){
          setTempDataId(result.tempDataId)
        }

        // Capture principal data if available in the response
        if (result.principal) {
          const familyMembers = (result.principal.familyMembers ||
            []) as PrincipalFamilyMemberResponseType[];

          const spouseObjData = familyMembers.find(
            (fMember) => fMember.relationship === "Spouse"
          );
          const beneficiaries = result.principal
            .beneficiaries as PrincipalBeneficiaryResponseType[];

          const principalAddress = result.principal
            .address as PrincipalInsuredAddressResponseType;

          const clientInformationStepperInitState = {
            stepperData: {
              principalinsured: transformPrincipalInsuredFromResponse(
                result.principal as PrincipalInsuredResponseType
              ),
              homeaddress:
                transformPrincipalInsuredAddressFromResponse(principalAddress, true),
              contactinformation:
                transformPrincipalInsuredContactInfoFromResponse(
                  result.principal
                    .contact as PrincipalInsuredContactInfoResponseType
                ),
              beneficiarydetails: {
                beneficiaries: beneficiaries.map(
                  (beneficiary: PrincipalBeneficiaryResponseType) =>
                    transformPrincipalBeneficiariesFromResponse(beneficiary)
                ),
              },
              familyinformation: {
                spouse: spouseObjData
                  ? transformPrincipalFamilyMemberFromResponse(
                      spouseObjData,
                      result.principal
                        .address as PrincipalInsuredAddressResponseType
                    )
                  : null,
                children: familyMembers
                  .filter((fMember) => fMember.relationship !== "Spouse")
                  .map((fMember) =>
                    transformPrincipalFamilyMemberFromResponse(
                      fMember,
                      principalAddress
                    )
                  ),
              },
            },
          };

          // save data from email validation to session storage
          sessionStorage.setItem(
            "clientInformationStepperState",
            JSON.stringify(clientInformationStepperInitState)
          );

          setPrincipalData({
            principalId: result.principal.principalId,
            principal_id: result.principal.principal_id,
            email: result.principal.email || email.trim(),
            ...result.principal,
          });
        }

        // Check if user is already verified
        if (result.isVerified === true) {
          // User is already verified, continue to next step
          console.log(
            "User is already verified, proceeding to client information"
          );
          setTimeout(() => {
            if (onNext) {
              onNext();
            }
          }, 1000);
        } else if (result.isVerified === false) {
          // User needs email verification, show email sent confirmation
          console.log("Email verification sent, showing confirmation page");
          setTimeout(() => {
            if (onEmailSent) {
              onEmailSent(email.trim());
            }
          }, 500);
        } else {
          // Fallback: if isVerified is undefined, assume email was sent
          console.log(
            "Email verification status unclear, showing confirmation page"
          );
          setTimeout(() => {
            if (onEmailSent) {
              onEmailSent(email.trim());
            }
          }, 500);
        }
      } else {
        // Error will be handled by the hook's error state
        console.error("Email verification failed:", result.message);
      }
    } catch (error) {
      console.error("Unexpected error during email verification:", error);
      setEmailError("An unexpected error occurred. Please try again.");
    }
  };

  const isValidateDisabled = !email.trim() || isValidating;

  // Display error message (only API error or local validation error)
  const displayError = apiError || emailError;

  return (
    <div className="email-validation-container">
      {/* Main Content */}
      <div className="email-validation-content">
        <h1 className="email-validation-title">
          Thank you for your interest in our
          <br />
          MyTravelsafe application
        </h1>

        <p className="email-validation-description">
          Please enter your e-mail address and
          <br />
          give us a few moments to validate your information.
        </p>

        {/* Email Form */}
        <div className="email-validation-form">
          <label htmlFor="email" className="email-label">
            E-mail
          </label>
          <input
            type="email"
            id="email"
            className={`email-input ${displayError ? "error" : ""}`}
            placeholder="olivia@email.com"
            value={email}
            onChange={handleEmailChange}
            disabled={isValidating}
            autoComplete="email"
            maxLength={EMAIL_MAX_LENGTH}
          />

          {/* Error message */}
          {displayError && (
            <div className="email-error" role="alert">
              {displayError}
            </div>
          )}

          <button
            type="button"
            className={`validate-button ${
              isValidateDisabled ? "disabled" : ""
            }`}
            onClick={handleValidate}
            disabled={isValidateDisabled}
          >
            {isValidating ? <>Validating</> : "Verify E-mail Address"}
          </button>
        </div>

        {/* Reminder Section */}
        <div className="reminder-section">
          <h3 className="reminder-title">Reminders</h3>
          <ul className="reminder-list">
            <li>
              If you are a new client or a first-time user of MyTravelsafe, we
              have sent you a verification e-mail via your nominated e-mail
              address. Please follow the steps in the e-mail to proceed.
            </li>
            <li>
              If you are a <strong>returning client</strong>, please give us a
              few moments to validate your information.
            </li>
          </ul>
        </div>

        {/* Contact Information */}
        <div className="contact-section">
          <p className="contact-text">
            Need assistance? E-mail us at
            <br />
            <a
              href="mailto:traveldirect_team@pacificcross.com.ph"
              className="contact-email"
            >
              traveldirect_team@pacificcross.com.ph
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailValidationSection;
